import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MenuItem } from 'primeng/api';
import { BehaviorSubject, filter } from 'rxjs';

interface NavState {
  menuLabelKey?: string;
  parentLabel?: string;
}

@Injectable({ providedIn: 'root' })
export class BreadcrumbService {
  private readonly _breadcrumbs$ = new BehaviorSubject<MenuItem[]>([]);
  readonly breadcrumbs$ = this._breadcrumbs$.asObservable();
  private currentBreadcrumbs: MenuItem[] = [];

  private readonly HOME_ITEM: MenuItem = {
    icon: 'pi pi-home',
    label: '',
    routerLink: '/inicio',
  };

  constructor(private router: Router, private translate: TranslateService) {
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        const breadcrumbs: MenuItem[] = [];
        this.addBreadcrumb(this.router.routerState.snapshot.root, [], breadcrumbs);

        // ✅ Leer el state que viene del menú al hacer click
        const navState = window.history.state as NavState;

        if (navState?.menuLabelKey && breadcrumbs.length > 0) {
          // Sobreescribir SOLO el label del último item con el del menú
          // routerLink queda intacto (la URL real de la ruta)
          const lastItem = breadcrumbs[breadcrumbs.length - 1];
          lastItem.id = navState.menuLabelKey;                           // guardamos clave para re-traducir
          lastItem.label = this.translate.instant(navState.menuLabelKey); // label visual del menú
        }

        this.currentBreadcrumbs = [this.HOME_ITEM, ...breadcrumbs];
        this._breadcrumbs$.next([...this.currentBreadcrumbs]);
      });

    // Refrescar traducciones si cambia el idioma
    this.translate.onLangChange.subscribe(() => {
      this.translateBreadcrumbs();
      this._breadcrumbs$.next([...this.currentBreadcrumbs]);
    });
  }

  private translateBreadcrumbs(): void {
    this.currentBreadcrumbs.forEach(item => {
      if (item.id) {
        // 'id' puede ser clave de menú (menu.idLabel.x) o de ruta (bc.path)
        item.label = this.translate.instant(item.id);
      }
    });
  }

  private addBreadcrumb(
    route: ActivatedRouteSnapshot,
    parentUrl: string[],
    breadcrumbs: MenuItem[]
  ): void {
    if (!route) return;

    const routeUrl = parentUrl.concat(route.url.map(segment => segment.path));
    const path = route.url.map(segment => segment.path).join('/');

    let i18nKey: string = route.data?.['i18n'];
    let icon :string = route.data?.['icon'];

    if (!i18nKey && path && !path.startsWith(':')) {
      i18nKey = `bc.${path}`;
    }

    if (i18nKey) {
      const label = this.translate.instant(i18nKey);
      const lastItem = breadcrumbs[breadcrumbs.length - 1];
      const isDuplicate = lastItem?.id === i18nKey;

      if (!isDuplicate) {
        breadcrumbs.push({
          id: i18nKey,                           // clave de ruta (fallback si no viene state)
          label,                                 // se sobreescribirá si viene menuLabelKey
          routerLink: '/' + routeUrl.join('/'),  // ← URL real, nunca se toca
          icon:icon
        });
      }
    }

    if (route.firstChild) {
      this.addBreadcrumb(route.firstChild, routeUrl, breadcrumbs);
    }
  }
}